import { useEffect, useMemo, useState } from "react";
import { useFilterAllTimeEntriesPublicQuery } from "app/api/absence";
import { useDebounce } from "../../hooks/useDebounce";
import { setDay, startOfDay } from "date-fns";
import { ScheduleScale } from "features/schedule-view/ScheduleContext";

export type Filters = {
  fromDate: Date;
  toDate: Date;
  departmentIds: number[] | null;
  officeIds: number[] | null;
};

const DEFAULT_FILTERS: Filters = {
  fromDate: startOfDay(new Date()),
  toDate: startOfDay(new Date()),
  departmentIds: null,
  officeIds: null,
};

type AbsenceSearchOptions = { view: "LIST" | ScheduleScale };

export const useAbsenceSearch = ({ view }: AbsenceSearchOptions) => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(DEFAULT_FILTERS.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_FILTERS.toDate);
  const [departmentIds, setDepartmentIds] = useState<number[] | null>(DEFAULT_FILTERS.departmentIds);
  const [officeIds, setOfficeIds] = useState<number[] | null>(DEFAULT_FILTERS.officeIds);

  const debouncedSearch = useDebounce(search, { delayMs: 500 });

  const activeFiltersCount = useMemo(
    () => Object.values({ departmentIds, officeIds }).filter((value) => Boolean(value)).length,
    [departmentIds, officeIds]
  );

  const { data, isFetching, isLoading } = useFilterAllTimeEntriesPublicQuery({
    searchQuery: debouncedSearch,
    fromDate: fromDate.toISOString(),
    toDate: toDate.toISOString(),
    departmentIds,
    officeIds,
  });

  const setFilters = ({ fromDate, toDate, departmentIds, officeIds }: Filters) => {
    // Align fromDate and toDate to week start/end in weekly views
    const adjustedFromDate =
      view === "WEEKS" || view === "WORKWEEKS"
        ? setDay(fromDate, 1, { weekStartsOn: 1 })
        : fromDate;
    const adjustedToDate =
      view === "WEEKS" || view === "WORKWEEKS"
        ? setDay(toDate, 0, { weekStartsOn: 1 })
        : toDate;

    setFromDate(adjustedFromDate);
    setToDate(adjustedToDate);
    setDepartmentIds(departmentIds);
    setOfficeIds(officeIds);
  };

  const resetFilters = () => {
    setFromDate(DEFAULT_FILTERS.fromDate);
    setToDate(DEFAULT_FILTERS.toDate);
    setDepartmentIds(DEFAULT_FILTERS.departmentIds);
    setOfficeIds(DEFAULT_FILTERS.officeIds);
  }

  // Ensure that fromDate and toDate are aligned to week start/end in weekly views
  useEffect(() => {
    if (view === "WEEKS" || view === "WORKWEEKS") {
      setFilters({ fromDate, toDate, departmentIds, officeIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  return {
    search,
    filters: { fromDate, toDate, departmentIds, officeIds },
    activeFiltersCount,
    data,
    isFetching,
    isLoading,
    setSearch,
    setFilters,
    resetFilters,
  };
};
